import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Facebook, Instagram, Twitter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { organizationInfo } from '../data/mock';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mock submission - simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message sent successfully! We\'ll get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: organizationInfo.address,
      link: null
    },
    {
      icon: Phone,
      title: 'Phone',
      content: organizationInfo.phone,
      link: `tel:${organizationInfo.phone}`
    },
    {
      icon: Mail,
      title: 'Email',
      content: organizationInfo.email,
      link: `mailto:${organizationInfo.email}`
    },
    {
      icon: Clock,
      title: 'Hours',
      content: 'Mon - Fri: 9:00 AM - 5:00 PM',
      link: null
    }
  ];

  const subjectOptions = [
    'General Inquiry',
    'Request Support',
    'Volunteer Interest',
    'Donation Question',
    'Partnership Inquiry',
    'Event Information',
    'Other'
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#5D4E6D] py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Contact</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Get in Touch
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              Have questions or need support? We're here to help. Reach out to us and 
              we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#2D2A32] mb-8">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((item) => {
                  const IconComponent = item.icon;
                  const content = (
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#E07A5F]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-[#E07A5F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#2D2A32]">{item.title}</h3>
                        <p className="text-gray-600">{item.content}</p>
                      </div>
                    </div>
                  );
                  
                  return item.link ? (
                    <a
                      key={item.title}
                      href={item.link}
                      className="block hover:opacity-75 transition-opacity"
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={item.title}>{content}</div>
                  );
                })}
              </div>

              {/* Social Links */}
              <div className="mt-10">
                <h3 className="font-semibold text-[#2D2A32] mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a
                    href={organizationInfo.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#5D4E6D] rounded-xl flex items-center justify-center text-white hover:bg-[#E07A5F] transition-colors duration-300"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={organizationInfo.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#5D4E6D] rounded-xl flex items-center justify-center text-white hover:bg-[#E07A5F] transition-colors duration-300"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href={organizationInfo.socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#5D4E6D] rounded-xl flex items-center justify-center text-white hover:bg-[#E07A5F] transition-colors duration-300"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#2D2A32] mb-6">Send us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-2 rounded-xl border-gray-200 focus:border-[#5D4E6D] focus:ring-[#5D4E6D]"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-2 rounded-xl border-gray-200 focus:border-[#5D4E6D] focus:ring-[#5D4E6D]"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-2 rounded-xl border-gray-200 focus:border-[#5D4E6D] focus:ring-[#5D4E6D]"
                          placeholder="(808) 555-0123"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Select
                          value={formData.subject}
                          onValueChange={(value) => setFormData({ ...formData, subject: value })}
                        >
                          <SelectTrigger className="mt-2 rounded-xl border-gray-200 focus:border-[#5D4E6D] focus:ring-[#5D4E6D]">
                            <SelectValue placeholder="Select a subject" />
                          </SelectTrigger>
                          <SelectContent>
                            {subjectOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="mt-2 rounded-xl border-gray-200 focus:border-[#5D4E6D] focus:ring-[#5D4E6D] resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full py-6 transition-all duration-300 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="h-96 bg-[#2D2A32] relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-16 h-16 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Located in Honolulu, Hawaii</h3>
            <p className="text-gray-400">Serving communities across the Hawaiian Islands</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
